/**
 * usePlacementHistorie - the Ausschreibungs-Screen Tab 6 read-only timeline (P2 Placement Slice 15).
 * useInfiniteQuery over next_cursor in live mode; a 12-entry fixture spanning every phase in mock mode.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { placementHistoriePhase, PLACEMENT_HISTORIE_PHASE_LABELS, type PlacementHistorieResponse, type PlacementHistorieEintrag } from '@brokly/shared';

const MOCK_EVENTS: Array<{ event: string; text: string }> = [
  { event: 'placement.created', text: 'Ausschreibung angelegt' },
  { event: 'placement.snapshot_frozen', text: 'Risikoprofil-Snapshot erstellt' },
  { event: 'submission.sent', text: 'Offertanfrage an Helvetia versendet' },
  { event: 'qna.asked', text: 'Rueckfrage des Versicherers eingegangen' },
  { event: 'offer.extracted', text: 'Offerte extrahiert' },
  { event: 'comparison.generated', text: 'Offertvergleich erstellt' },
  { event: 'customer.decided', text: 'Kundenentscheid erfasst' },
  { event: 'application.prepared', text: 'Antrag vorbereitet' },
  { event: 'application.police_received', text: 'Police erhalten' },
  { event: 'policy.control.reported_to_placement', text: 'Police der Ausschreibung zugeordnet' },
  { event: 'placement.policy_control.completed', text: 'Policenkontrolle 1: abweichung' },
  { event: 'policy.control.discrepancy_detected', text: 'Abweichung erkannt - Klaerungsaufgabe erstellt' },
];

function mockHistorie(id: string): PlacementHistorieResponse {
  const base = Date.parse('2026-11-20T09:00:00.000Z');
  const eintraege: PlacementHistorieEintrag[] = MOCK_EVENTS.map((e, i) => {
    const phase = placementHistoriePhase(e.event);
    return {
      id: `mock-${i}`,
      quelle: 'aktivitaet' as const,
      zeitpunkt: new Date(base - (MOCK_EVENTS.length - i) * 3600_000).toISOString(),
      text: e.text,
      event: e.event,
      action: null,
      entity_type: null,
      entity_id: null,
      actor_kind: i % 2 === 0 ? ('human' as const) : ('system' as const),
      actor_name: i % 2 === 0 ? 'Berater B' : null,
      kontext: {},
      phase,
      phase_label: PLACEMENT_HISTORIE_PHASE_LABELS[phase],
    };
  }).reverse();
  return { placement_id: id, placement_number: 'PL-2026-000512', eintraege, next_cursor: null, quelle: 'aktivitaet', audit_hidden_reason: null };
}

export function usePlacementHistorie(id: string, quelle: 'aktivitaet' | 'alle', enabled = true) {
  const live = isLiveData();
  return useInfiniteQuery<PlacementHistorieResponse>({
    queryKey: qk.placementHistorie(id, quelle, live ? 'live' : 'mock'),
    queryFn: ({ pageParam }) =>
      live
        ? apiClient.placements.getHistorie(id, { quelle, cursor: (pageParam as string | undefined) ?? undefined })
        : Promise.resolve(mockHistorie(id)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    ...(live ? {} : { initialData: { pages: [mockHistorie(id)], pageParams: [undefined] }, staleTime: Infinity }),
  });
}
